import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, FileText } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { usePurchaseOrders } from '@/hooks/use-procurement';
import type { PurchaseOrderStatus } from '@/lib/types';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';

const statusColors: Record<PurchaseOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  sent: 'bg-indigo-100 text-indigo-800',
  partially_received: 'bg-purple-100 text-purple-800',
  received: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels: Record<PurchaseOrderStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sent: 'Sent',
  partially_received: 'Partially Received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const PAGE_SIZE = 20;

export function PurchaseOrdersListPage() {
  const timeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PurchaseOrderStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = usePurchaseOrders({
    q: search,
    status,
    page,
    page_size: PAGE_SIZE,
  });

  const orders = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Purchase Orders
          </h1>
          <p className="text-gray-600 mt-1">
            Create, approve, and track purchase orders
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <Button asChild>
          <Link to="/purchase-orders/new">
            <Plus className="h-4 w-4 mr-2" />
            New Purchase Order
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by PO # or notes..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as PurchaseOrderStatus | '');
                setPage(1);
              }}
              className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="sent">Sent</option>
              <option value="partially_received">Partially Received</option>
              <option value="received">Received</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load purchase orders. Please try again.
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No purchase orders found.</p>
              <Button asChild className="mt-4">
                <Link to="/purchase-orders/new">
                  Create your first purchase order
                </Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Expected Delivery</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((po) => (
                  <TableRow key={po.id}>
                    <TableCell className="font-mono text-sm">
                      <Link
                        to={`/purchase-orders/${po.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {po.po_number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {po.vendor_name}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[po.status]}>
                        {statusLabels[po.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(po.total, po.currency || orgCurrency)}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {po.expected_delivery
                        ? formatDateOnly(po.expected_delivery)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateTimeInTimeZone(po.created_at, timeZone, { dateStyle: 'medium' })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!data?.previous}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!data?.next}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
